import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, Send } from 'lucide-react';
import { whatsappLink } from '@/shared/lib/format';
import type { IndividualMessage, Lead } from '@/shared/types';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Card } from '@/shared/ui/card';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { MessageFieldWithEmoji } from '@/features/campaigns/ui/message-field-with-emoji';
import { fetchIndividualMessages, sendIndividualMessage } from '../api/use-leads';
import {
  IndividualMessagesTimeline,
  IndividualMessagesTimelineHeading,
} from './individual-messages-timeline';

type MessageChannel = 'push' | 'whatsapp';

interface LeadMessagesTabProps {
  idPassbook: string;
  lead: Lead;
}

export function LeadMessagesTab({ idPassbook, lead }: LeadMessagesTabProps) {
  const [channel, setChannel] = useState<MessageChannel>('push');
  const [message, setMessage] = useState('Seu cartão foi atualizado!');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<IndividualMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMessages = async () => {
    if (!lead.profile?.uniqueId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const data = await fetchIndividualMessages(idPassbook, lead.profile.uniqueId);
    setMessages(data);
    setLoading(false);
  };

  useEffect(() => {
    void loadMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPassbook, lead.profile?.uniqueId]);

  const handleSend = async () => {
    const text = message.trim();
    if (!text) {
      toast.error('Digite uma mensagem.');
      return;
    }

    if (channel === 'whatsapp') {
      if (!lead.phone) {
        toast.error('Lead sem telefone para WhatsApp.');
        return;
      }
      window.open(whatsappLink(lead.phone, true, text), '_blank', 'noreferrer');
      return;
    }

    if (!lead.serialNumber || !lead.profile?.uniqueId) {
      toast.error('Lead sem cartão digital para envio push.');
      return;
    }

    setSending(true);
    try {
      await sendIndividualMessage(idPassbook, {
        serialNumber: lead.serialNumber,
        uniqueId: lead.profile.uniqueId,
        email: lead.email,
        phone: lead.phone,
        description: text,
        current_stamps: lead.total_points ?? 0,
        birthday: lead.birthday,
        gender: lead.gender,
      });
      toast.success('Mensagem enviada.');
      setMessage('Seu cartão foi atualizado!');
      await loadMessages();
    } catch {
      toast.error('Falha ao enviar mensagem.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="space-y-4 p-6">
        <div className="space-y-3">
          <Label>Enviar mensagem por</Label>
          <RadioGroup
            value={channel}
            onValueChange={(v) => setChannel(v as MessageChannel)}
            className="flex flex-wrap gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="push" id="msg-push" />
              <Label htmlFor="msg-push" className="cursor-pointer font-normal">
                Push (cartão digital)
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="whatsapp" id="msg-wa" />
              <Label htmlFor="msg-wa" className="cursor-pointer font-normal">
                WhatsApp
              </Label>
            </div>
          </RadioGroup>
        </div>

        <MessageFieldWithEmoji
          id="msg-body"
          label="Mensagem"
          value={message}
          onChange={setMessage}
          rows={8}
          placeholder="Digite a mensagem"
        />

        <Button onClick={handleSend} loading={sending} disabled={channel === 'whatsapp' && !lead.phone}>
          {channel === 'whatsapp' ? (
            <>
              <MessageCircle className="size-4" /> Abrir WhatsApp
            </>
          ) : (
            <>
              <Send className="size-4" /> Enviar
            </>
          )}
        </Button>

        {channel === 'push' && !lead.serialNumber ? (
          <p className="text-sm text-muted-foreground">Este lead não possui cartão digital para push.</p>
        ) : null}
      </Card>

      <Card className="p-6">
        <IndividualMessagesTimelineHeading>Últimas mensagens enviadas</IndividualMessagesTimelineHeading>
        <IndividualMessagesTimeline
          messages={messages}
          loading={loading}
          emptyMessage="Nenhuma mensagem enviada ainda."
        />
      </Card>
    </div>
  );
}
